import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';

const SUBJECT_OPTIONS = [
  { value: 'job_posting', label: 'Job Posting Enquiry' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

function charCountClass(length) {
  if (length >= 2000) return 'rwm-contact__char-count--danger';
  if (length >= 1900) return 'rwm-contact__char-count--warning';
  return '';
}

export default function ContactPage() {
  const { apiBase } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientErrors, setClientErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!showSuccess) return undefined;
    const timer = setTimeout(() => setShowSuccess(false), 8000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  const validateClient = () => {
    const next = {};
    if (!name.trim()) next.name = 'Name is required.';
    if (!email.trim()) next.email = 'Email is required.';
    if (!subject) next.subject = 'Please select a subject.';
    if (!message.trim()) {
      next.message = 'Message is required.';
    } else if (message.trim().length < 20) {
      next.message = 'Message must be at least 20 characters.';
    }
    setClientErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setFieldErrors({});
    if (!validateClient()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/api/v1/contact`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 422 && data.errors) {
        setFieldErrors(data.errors);
        return;
      }

      if (!res.ok) {
        setShowError(true);
        return;
      }

      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setClientErrors({});
      setShowSuccess(true);
    } catch {
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const messageLength = message.length;

  return (
    <Container className="py-5" data-testid="rwm-contact-page">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <h1 className="fw-bold mb-2" style={{ color: 'var(--rwm-dark)' }}>
            Contact Us
          </h1>
          <p className="lead mb-4" style={{ color: 'var(--rwm-slate)' }}>
            Have a question? We&apos;d love to hear from you.
          </p>

          {showSuccess ? (
            <Alert variant="success" data-testid="rwm-contact-success">
              ✓ Message sent! We&apos;ll get back to you within 2 business days.
            </Alert>
          ) : null}

          {showError ? (
            <Alert variant="danger" data-testid="rwm-contact-error">
              Something went wrong. Please try again.
            </Alert>
          ) : null}

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="rwm-contact-name-field">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                data-testid="rwm-contact-name"
              />
              {clientErrors.name ? (
                <div className="rwm-auth__field-error mt-1">{clientErrors.name}</div>
              ) : null}
              {fieldErrors.name ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.name[0]}</div>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3" controlId="rwm-contact-email-field">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                data-testid="rwm-contact-email"
              />
              {clientErrors.email ? (
                <div className="rwm-auth__field-error mt-1">{clientErrors.email}</div>
              ) : null}
              {fieldErrors.email ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.email[0]}</div>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3" controlId="rwm-contact-subject-field">
              <Form.Label>
                Subject <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="rwm-contact-subject"
              >
                <option value="" disabled>
                  -- Select a subject --
                </option>
                {SUBJECT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              {clientErrors.subject ? (
                <div className="rwm-auth__field-error mt-1">{clientErrors.subject}</div>
              ) : null}
              {fieldErrors.subject ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.subject[0]}</div>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3" controlId="rwm-contact-message-field">
              <Form.Label>
                Message <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                data-testid="rwm-contact-message"
              />
              <div
                className={`small text-end mt-1 ${charCountClass(messageLength)}`}
                style={messageLength < 1900 ? { color: 'var(--rwm-slate)' } : undefined}
                data-testid="rwm-contact-char-count"
              >
                {messageLength} / 2000
              </div>
              {clientErrors.message ? (
                <div className="rwm-auth__field-error mt-1">{clientErrors.message}</div>
              ) : null}
              {fieldErrors.message ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.message[0]}</div>
              ) : null}
            </Form.Group>

            <Button
              type="submit"
              variant="info"
              className="w-100 w-md-auto text-white"
              disabled={submitting}
              data-testid="rwm-contact-submit"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" role="status" />
                  Sending…
                </>
              ) : (
                'Send Message'
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}
